/**
 * Universal AI Search Component - Generic Version for All Projects
 * Sprint 1, Task 11: Added feedback integration
 * 
 * Features:
 * - Fast entity search with intelligent query routing
 * - AI-powered insights via RAG
 * - Project-agnostic (uses current project from workspace context)
 * - Search history with local storage
 * - Sample queries and suggestions
 * - Performance metrics display
 */

import React, { useState, useEffect } from 'react';
import { Search, Zap, Brain, Clock, List, CheckSquare, Square, StopCircle, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { queryEntities, queryEntitiesBulk } from '../../api/entities';
import { useStreamingRAG } from '../../hooks/useStreamingRAG';
import { Card } from '../UI/Card';
import { Input } from '../UI/Input';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { EmptyState } from '../UI/EmptyState';
import { useSearchConfig, EntityTypeConfig } from './SearchConfigProvider';
import { FeedbackWidget } from '../Feedback/FeedbackWidget';
import { FeedbackModal } from '../Feedback/FeedbackModal';

interface SearchResult {
  category: string;
  entities: any[];
  count: number;
}

/**
 * Comprehensive keyword-based routing for entity types
 * Uses priority scoring to rank entity types by relevance
 */
function routeQueryToEntityTypes(query: string, entityTypes: EntityTypeConfig[]): EntityTypeConfig[] {
  const queryLower = query.toLowerCase();
  const matches: Array<{ config: EntityTypeConfig; score: number; matchedKeywords: number }> = [];

  entityTypes.forEach((config) => {
    if (!config.enabled) return;

    let score = 0;
    let matchedKeywords = 0;
    
    // Count keyword matches with priority weighting
    config.keywords.forEach((keyword) => {
      if (queryLower.includes(keyword.toLowerCase())) {
        // Higher priority for exact phrase matches
        if (queryLower === keyword.toLowerCase()) {
          score += 10;
        } else {
          score += 2;
        }
        matchedKeywords++;
      }
    });

    if (score > 0) {
      matches.push({ config, score, matchedKeywords });
    }
  });

  // Sort by score (descending), then by matched keywords (descending)
  matches.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return b.matchedKeywords - a.matchedKeywords;
  });

  // If we have matches, return top 10 (increased from 8 for more comprehensive results)
  if (matches.length > 0) {
    const topMatches = matches.slice(0, 10).map((m) => m.config);
    console.log('[UniversalSearch] Query routing matched', topMatches.length, 'entity types:', 
      topMatches.map(t => `${t.label} (score: ${matches.find(m => m.config.id === t.id)?.score})`).join(', '));
    return topMatches;
  }

  // Default: return top 8 enabled types (reduced from 10 to be more targeted)
  console.log('[UniversalSearch] No keyword matches, using default entity types');
  return entityTypes.filter((t) => t.enabled).slice(0, 8);
}

export function UniversalSearch() {
  const workspace = useWorkspace();
  const { projectId } = workspace;
  const { config: searchConfig, fetchEntityTypesIfNeeded } = useSearchConfig();
  
  // Trigger entity type fetch when search page is accessed (lazy loading)
  useEffect(() => {
    if (projectId) {
      fetchEntityTypesIfNeeded();
    }
  }, [projectId, fetchEntityTypesIfNeeded]);
  
  // Debug logging for projectId changes
  useEffect(() => {
    if (projectId) {
      console.log('[UniversalSearch] Current projectId:', projectId);
    } else {
      console.warn('[UniversalSearch] No projectId available');
    }
  }, [projectId]);
  const [query, setQuery] = useState('');
  const [context, setContext] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searchTime, setSearchTime] = useState(0);
  const [showConfig, setShowConfig] = useState(false);
  const [entityTypes, setEntityTypes] = useState<EntityTypeConfig[]>(searchConfig.entityTypes);
  const [queriesToRun, setQueriesToRun] = useState<string[]>([]);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showDetailedFeedback, setShowDetailedFeedback] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [expandedCitation, setExpandedCitation] = useState<number | null>(null);
  
  // Streaming RAG hook for AI insights
  const aiStreaming = useStreamingRAG();
  
  // Loading messages that rotate
  const loadingMessages = [
    '🔍 Searching through knowledge base...',
    '📚 Analyzing documents...',
    '🧠 Processing with AI...',
    '💡 Generating insights...',
    '✨ Almost there...',
    '🎯 Finding the best evidence...',
  ];

  // Update entity types when search config changes (e.g., plugin registers new types)
  useEffect(() => {
    setEntityTypes(searchConfig.entityTypes);
  }, [searchConfig.entityTypes]);
  
  // Rotate loading messages while streaming
  useEffect(() => {
    if (!aiStreaming.isStreaming) return;
    
    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000); // Change message every 2 seconds
    
    return () => clearInterval(interval);
  }, [aiStreaming.isStreaming, loadingMessages.length]);

  // Load search history from localStorage on mount
  useEffect(() => {
    const history = localStorage.getItem('universal-search-history');
    if (history) {
      try {
        setSearchHistory(JSON.parse(history));
      } catch (e) {
        console.error('Error loading search history:', e);
      }
    }
  }, []);

  // Save search to history
  const saveToHistory = (searchQuery: string) => {
    const updated = [searchQuery, ...searchHistory.filter((q) => q !== searchQuery)].slice(0, 20);
    setSearchHistory(updated);
    localStorage.setItem('universal-search-history', JSON.stringify(updated));
  };

  // Get suggestions based on current input
  const getSuggestions = () => {
    console.log('[UniversalSearch] Getting suggestions. Sample queries available:', searchConfig.sampleQueries);
    if (!query.trim()) return searchConfig.sampleQueries;

    const queryLower = query.toLowerCase();
    const fromHistory = searchHistory.filter((h) => h.toLowerCase().includes(queryLower));
    const fromSamples = searchConfig.sampleQueries.filter((s) => s.toLowerCase().includes(queryLower));

    return [...fromHistory, ...fromSamples].slice(0, 8);
  };

  const toggleEntityType = (id: string) => {
    setEntityTypes((prev) => prev.map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t)));
  };

  const toggleAll = (enable: boolean) => {
    setEntityTypes((prev) => prev.map((t) => ({ ...t, enabled: enable })));
  };

  const handleSearch = async () => {
    // Validate projectId is available - get fresh value from workspace store
    const currentProjectId = workspace.projectId;
    
    if (!query.trim()) {
      return;
    }
    
    if (!currentProjectId) {
      console.error('[UniversalSearch] No projectId available. Cannot search.');
      return;
    }
    
    // Log for debugging
    console.log('[UniversalSearch] Starting search with projectId:', currentProjectId);

    // Save to history
    saveToHistory(query);
    setShowSuggestions(false);

    setSearching(true);
    setResults([]);
    aiStreaming.reset();  // Reset streaming state
    setQueriesToRun([]);
    const startTime = Date.now();

    try {
      // Intelligent query routing
      const routedTypes = routeQueryToEntityTypes(query, entityTypes);
      setQueriesToRun(routedTypes.map((t) => t.label));

      console.log(`[UniversalSearch] Querying ${routedTypes.length} entity types for project ${currentProjectId}`);
      console.log(`[UniversalSearch] Using BULK query endpoint for better performance and debugging`);

      // Phase 1: Fast entity search - USE BULK ENDPOINT (single request instead of N requests)
      const entityResults: SearchResult[] = [];

      try {
        const bulkResponse = await queryEntitiesBulk({
          project_id: currentProjectId,
          entity_types: routedTypes.map(t => t.entityType),
          limit_per_type: 100, // Increased from 20 to 100 for comprehensive results
          offset_per_type: 0,
        });

        console.log(`[UniversalSearch] Bulk query complete: ${bulkResponse.total_entity_types} types with results, ` +
                    `${bulkResponse.total_entities_returned} entities in ${bulkResponse.execution_time_ms.toFixed(1)}ms`);

        // Convert bulk response to SearchResult format
        for (const typeResult of bulkResponse.results) {
          // Find the matching config for label
          const typeConfig = routedTypes.find(t => t.entityType === typeResult.entity_type);
          if (typeConfig && typeResult.entities.length > 0) {
            entityResults.push({
              category: typeConfig.label,
              entities: typeResult.entities.slice(0, 10),
              count: typeResult.total_count,
            });
          }
        }

        // Log which entity types had results
        if (entityResults.length > 0) {
          console.log(`[UniversalSearch] Entity types with results:`, 
            entityResults.map(r => `${r.category} (${r.count})`).join(', '));
        } else {
          console.warn(`[UniversalSearch] No entities found for any of the ${routedTypes.length} queried types`);
        }

      } catch (error) {
        console.error(`[UniversalSearch] Bulk query error:`, error);
        // Fallback to empty results on error
      }

      setResults(entityResults);
      setSearchTime((Date.now() - startTime) / 1000);
      setSearching(false);

      // Phase 2: AI Insights (streaming) - Increased max_results for comprehensive evidence
      aiStreaming.startQuery(currentProjectId, {
        question: context ? `${query}. Context: ${context}` : query,
        mode: 'auto',
        require_evidence: true,
        max_results: 50, // Increased from 10 to 50 for better evidence coverage
      });
    } catch (error) {
      console.error('[UniversalSearch] Search error:', error);
      // Show user-friendly error instead of crashing
      setResults([]);
      setSearching(false);
    }
  };

  const handleCancelAI = () => {
    aiStreaming.cancelQuery();
  };

  if (!projectId) {
    return (
      <EmptyState
        icon={<Search className="h-12 w-12" />}
        title="No Project Selected"
        description="Please select a project to use Universal AI Search"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-500 dark:to-primary-600 rounded-xl p-8 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 backdrop-blur-sm rounded-lg">
            <Search className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Universal AI Search</h1>
            <p className="text-primary-100 mt-1">Fast entity search + AI-powered insights</p>
          </div>
        </div>
      </div>

      {/* Search Input */}
      <Card>
        <div className="space-y-4">
          <div className="relative">
            <label className="block text-sm font-semibold text-theme-text mb-2">Search Query</label>
            <Input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowSuggestions(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                } else if (e.key === 'Escape') {
                  setShowSuggestions(false);
                }
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="e.g., Show me critical alerts, Recent incidents, Upcoming deadlines..."
              leftIcon={<Search className="h-5 w-5 text-gray-400" />}
            />

            {/* Suggestions Dropdown */}
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-indigo-200 rounded-lg shadow-xl z-10 max-h-96 overflow-y-auto">
                <div className="p-3 bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-700">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-primary-900 dark:text-primary-100">
                      {query.trim() ? 'Matching Searches' : 'Sample Queries & History'}
                    </span>
                    <button
                      onClick={() => setShowSuggestions(false)}
                      className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      Close
                    </button>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {getSuggestions().map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setQuery(suggestion);
                        setShowSuggestions(false);
                        setTimeout(() => handleSearch(), 100);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition-all group"
                    >
                      <div className="flex items-center gap-3">
                        <Search className="h-4 w-4 text-theme-text-disabled group-hover:text-primary-600" />
                        <span className="text-sm text-theme-text group-hover:text-primary-900 dark:group-hover:text-primary-100 group-hover:font-medium">
                          {suggestion}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-theme-text mb-2">Context (Optional)</label>
            <Input
              type="text"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="e.g., Focus on compliance risks, Prepare for audit..."
            />
          </div>

          <div className="flex items-center gap-4">
            <Button
              onClick={handleSearch}
              isLoading={searching}
              disabled={!query.trim()}
              className="flex-1"
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>

            <Button onClick={() => setShowConfig(!showConfig)} variant="secondary">
              <List className="h-4 w-4 mr-2" />
              Configure ({entityTypes.filter((t) => t.enabled).length}/{entityTypes.length})
            </Button>
          </div>
        </div>
      </Card>

      {/* Configuration Panel */}
      {showConfig && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-theme-text">Entity Types to Search</h3>
            <div className="flex gap-2">
              <Button onClick={() => toggleAll(true)} size="sm" variant="secondary">
                Enable All
              </Button>
              <Button onClick={() => toggleAll(false)} size="sm" variant="secondary">
                Disable All
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {entityTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => toggleEntityType(type.id)}
                className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 transition-all text-left"
              >
                {type.enabled ? (
                  <CheckSquare className="h-5 w-5 text-green-600 flex-shrink-0" />
                ) : (
                  <Square className="h-5 w-5 text-theme-text-disabled flex-shrink-0" />
                )}
                <span className={`text-sm ${type.enabled ? 'text-theme-text font-medium' : 'text-theme-text-secondary'}`}>
                  {type.label}
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Search History */}
      {searchHistory.length > 0 && !searching && results.length === 0 && (
        <Card className="bg-purple-50 border-purple-200">
          <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Recent Searches ({searchHistory.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {searchHistory.slice(0, 10).map((hist, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setQuery(hist);
                  setTimeout(() => handleSearch(), 100);
                }}
                className="px-3 py-1.5 bg-purple-100 text-purple-800 rounded-lg text-xs font-semibold hover:bg-purple-200 transition-all"
              >
                {hist.length > 50 ? hist.substring(0, 47) + '...' : hist}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Queries to Run Display */}
      {queriesToRun.length > 0 && (
        <Card className="bg-green-50 border-green-200">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-green-900 flex items-center gap-2">
              <List className="h-4 w-4" />
              Smart Query Routing
            </h3>
            <Badge variant="success">{queriesToRun.length} entity types</Badge>
          </div>
          <p className="text-sm text-green-700 mb-3">
            Based on your query keywords, searching these entity types:
          </p>
          <div className="flex flex-wrap gap-2">
            {queriesToRun.map((name, idx) => (
              <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                {name}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* AI Insights - Streaming with real-time updates */}
      {(aiStreaming.isStreaming || aiStreaming.finalAnswer || aiStreaming.partialAnswer) && (
        <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
          <div className="flex items-center gap-3 mb-4">
            <Brain className={`h-6 w-6 text-purple-600 ${aiStreaming.isStreaming ? 'animate-pulse' : ''}`} />
            <h3 className="text-lg font-bold text-theme-text">AI Analysis</h3>
            {aiStreaming.citations.length > 0 && (
              <Badge variant="default" className="bg-purple-100 text-purple-800">
                {aiStreaming.citations.length} citation{aiStreaming.citations.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {aiStreaming.isStreaming && (
              <button
                onClick={handleCancelAI}
                className="ml-auto flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
              >
                <StopCircle className="h-4 w-4" />
                Cancel
              </button>
            )}
            {!aiStreaming.isStreaming && aiStreaming.totalTime > 0 && (
              <span className="ml-auto text-sm text-purple-600 font-semibold">
                {aiStreaming.totalTime.toFixed(2)}s
              </span>
            )}
          </div>
          
          {/* Enhanced Streaming status indicator with rotating messages */}
          {aiStreaming.isStreaming && (
            <div className="mb-4 p-4 bg-gradient-to-r from-purple-100 via-indigo-100 to-purple-100 rounded-lg border-2 border-purple-300 shadow-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="relative">
                  {aiStreaming.stage === 'retrieval' || aiStreaming.stage === 'started' ? (
                    <Search className="h-5 w-5 text-purple-600 animate-bounce" />
                  ) : aiStreaming.stage === 'thinking' ? (
                    <Brain className="h-5 w-5 text-purple-600 animate-pulse" />
                  ) : (
                    <Zap className="h-5 w-5 text-purple-600 animate-spin" />
                  )}
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
                  </span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-purple-900 mb-1">
                    {aiStreaming.message}
                  </div>
                  <div className="text-xs text-purple-700 animate-pulse">
                    {loadingMessages[loadingMessageIndex]}
                  </div>
                </div>
                {aiStreaming.elapsed > 0 && (
                  <div className="text-right">
                    <div className="text-lg font-bold text-purple-600">{aiStreaming.elapsed.toFixed(1)}s</div>
                    <div className="text-xs text-purple-500">elapsed</div>
                  </div>
                )}
              </div>
              
              {/* Enhanced Progress bar with stages */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-purple-700 font-medium">
                  <span className={aiStreaming.stage === 'retrieval' || aiStreaming.stage === 'started' ? 'text-purple-900' : ''}>
                    🔍 Search
                  </span>
                  <span className={aiStreaming.stage === 'thinking' ? 'text-purple-900' : ''}>
                    🧠 Think
                  </span>
                  <span className={aiStreaming.stage === 'generating' ? 'text-purple-900' : ''}>
                    ✍️ Write
                  </span>
                  <span className={aiStreaming.stage === 'complete' ? 'text-purple-900' : ''}>
                    ✅ Done
                  </span>
                </div>
                <div className="h-2 bg-purple-200 rounded-full overflow-hidden shadow-inner">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-600 transition-all duration-500 ease-out relative overflow-hidden"
                    style={{
                      width: aiStreaming.stage === 'retrieval' || aiStreaming.stage === 'started' ? '25%' :
                             aiStreaming.stage === 'thinking' ? '50%' :
                             aiStreaming.stage === 'generating' ? '75%' :
                             aiStreaming.stage === 'complete' ? '100%' : '10%',
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
                  </div>
                </div>
              </div>
              
              {aiStreaming.reasoningTokens > 0 && (
                <div className="mt-3 flex items-center gap-2 text-xs text-purple-700 bg-purple-50 px-3 py-2 rounded-md">
                  <Brain className="h-3 w-3" />
                  <span className="font-medium">{aiStreaming.reasoningTokens} reasoning tokens</span>
                  <span className="text-purple-500">• Deep thinking mode active</span>
                </div>
              )}
            </div>
          )}
          
          {/* Answer content (streaming or final) */}
          <div className="prose prose-sm max-w-none mb-4">
            <p className="text-theme-text leading-relaxed whitespace-pre-wrap">
              {aiStreaming.finalAnswer || aiStreaming.partialAnswer || 'Analyzing...'}
              {aiStreaming.isStreaming && aiStreaming.stage === 'generating' && (
                <span className="inline-block w-2 h-4 bg-purple-500 ml-1 animate-pulse" />
              )}
            </p>
          </div>
          
          {/* Enhanced Citations Section with Artifact Details */}
          {aiStreaming.citations.length > 0 && !aiStreaming.isStreaming && (
            <div className="mt-6 pt-6 border-t border-purple-200">
              <h4 className="text-base font-bold text-purple-900 mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600" />
                Evidence Sources ({aiStreaming.citations.length})
              </h4>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {aiStreaming.citations.map((citation, idx) => {
                  const isExpanded = expandedCitation === idx;
                  
                  return (
                    <div
                      key={idx}
                      className="bg-gradient-to-r from-white to-purple-50 rounded-lg border-2 border-purple-200 hover:border-purple-400 transition-all shadow-sm hover:shadow-md"
                    >
                      {/* Citation Header */}
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-sm">
                              {citation.index || idx + 1}
                            </div>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            {/* Source Document Info */}
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <FileText className="h-4 w-4 text-purple-600 flex-shrink-0" />
                              <span className="text-sm font-bold text-theme-text truncate">
                                {citation.source || citation.artifact_name || 'Unknown Document'}
                              </span>
                              {citation.confidence && (
                                <Badge variant="default" className="bg-green-100 text-green-800 text-xs">
                                  {(citation.confidence * 100).toFixed(0)}% match
                                </Badge>
                              )}
                            </div>
                            
                            {/* Metadata Tags */}
                            <div className="flex items-center gap-2 mb-2 flex-wrap text-xs">
                              {citation.page && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md font-medium">
                                  📄 Page {citation.page}
                                </span>
                              )}
                              {citation.section && (
                                <span className="px-2 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200 rounded-md font-medium">
                                  📑 {citation.section}
                                </span>
                              )}
                              {citation.chunk_id && (
                                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-md font-mono text-xs">
                                  ID: {citation.chunk_id.substring(0, 8)}...
                                </span>
                              )}
                            </div>
                            
                            {/* Preview Content */}
                            {citation.content && (
                              <div className="mt-3 p-3 bg-white rounded-md border border-purple-100">
                                <div className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1">
                                  <span>📝</span>
                                  <span>Relevant Excerpt:</span>
                                </div>
                                <div className={`text-sm text-theme-text leading-relaxed ${!isExpanded ? 'line-clamp-3' : ''}`}>
                                  "{citation.content}"
                                </div>
                                {citation.content.length > 200 && (
                                  <button
                                    onClick={() => setExpandedCitation(isExpanded ? null : idx)}
                                    className="mt-2 text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
                                  >
                                    {isExpanded ? (
                                      <>
                                        <ChevronDown className="h-3 w-3" />
                                        Show less
                                      </>
                                    ) : (
                                      <>
                                        <ChevronRight className="h-3 w-3" />
                                        Read more
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="mt-3 flex items-center gap-2">
                              {citation.artifact_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    // Navigate to Knowledge Base with this artifact
                                    window.location.href = `/knowledge?artifact=${citation.artifact_id}`;
                                  }}
                                  className="text-xs"
                                >
                                  <FileText className="h-3 w-3 mr-1" />
                                  View Document
                                </Button>
                              )}
                              {citation.chunk_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    // Navigate to Evidence Viewer with this chunk
                                    window.location.href = `/evidence?chunk=${citation.chunk_id}`;
                                  }}
                                  className="text-xs"
                                >
                                  <Search className="h-3 w-3 mr-1" />
                                  View Chunk
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Help Text */}
              <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-xs text-purple-700">
                  💡 <span className="font-semibold">Tip:</span> Click "View Document" to see the full source file, or "View Chunk" to explore the specific evidence section.
                </p>
              </div>
              
              {/* Feedback Widget for AI Insights */}
              {projectId && aiStreaming.finalAnswer && (
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <FeedbackWidget
                    queryId={`search-${Date.now()}`}
                    projectId={projectId}
                    answer={aiStreaming.finalAnswer}
                    citations={aiStreaming.citations}
                    compact={false}
                    onFeedbackSubmit={() => {
                      console.log('[FEEDBACK] Thank you for your feedback!');
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Feedback Modal */}
      {showDetailedFeedback && projectId && aiStreaming.finalAnswer && (
        <FeedbackModal
          isOpen={showDetailedFeedback}
          onClose={() => setShowDetailedFeedback(false)}
          queryId={`search-${Date.now()}`}
          projectId={projectId}
          answer={aiStreaming.finalAnswer}
          citations={aiStreaming.citations}
        />
      )}

      {/* Loading State */}
      {searching && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">Searching...</span>
        </div>
      )}

      {/* Results */}
      {!searching && results.length > 0 && (
        <div className="space-y-6">
          {/* Performance Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-green-50 border-green-200">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <Zap className="h-4 w-4" />
                <span className="text-xs font-semibold">Fast Search</span>
              </div>
              <div className="text-2xl font-bold text-green-900">{searchTime.toFixed(2)}s</div>
              <div className="text-xs text-green-700 mt-1">{results.length} entity types</div>
            </Card>

            <Card className="bg-purple-50 border-purple-200">
              <div className="flex items-center gap-2 text-purple-700 mb-1">
                <Brain className="h-4 w-4" />
                <span className="text-xs font-semibold">AI Insights</span>
              </div>
              <div className="text-2xl font-bold text-purple-900">{aiStreaming.totalTime.toFixed(2)}s</div>
              <div className="text-xs text-purple-700 mt-1">
                {aiStreaming.reasoningTokens > 0 ? `${aiStreaming.reasoningTokens} reasoning` : 'Generated'}
              </div>
            </Card>

            <Card className="bg-blue-50 border-blue-200">
              <div className="flex items-center gap-2 text-blue-700 mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-xs font-semibold">Total Time</span>
              </div>
              <div className="text-2xl font-bold text-blue-900">{(searchTime + aiStreaming.totalTime).toFixed(2)}s</div>
              <div className="text-xs text-blue-700 mt-1">End-to-end</div>
            </Card>
          </div>

          {/* Entity Results */}
          {results.map((result) => (
            <Card key={result.category}>
              <div className="mb-4 pb-4 border-b border-theme-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-theme-text">{result.category}</h3>
                  <Badge>{result.count} results</Badge>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {result.entities.map((entity) => (
                  <div
                    key={entity.id}
                    className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-white transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="text-sm font-semibold text-theme-text">{entity.entity_type}</div>
                      <div className="text-xs text-theme-text-secondary font-mono">{entity.id?.substring(0, 8)}</div>
                    </div>
                    <div className="text-sm text-theme-text space-y-2">
                      {entity.data?.entity_data && typeof entity.data.entity_data === 'object' ? (
                        Object.entries(entity.data.entity_data as Record<string, any>)
                          .slice(0, 6)
                          .map(([key, value]) => (
                            <div key={key} className="flex gap-2">
                              <span className="font-medium text-theme-text-secondary min-w-[100px]">{key}:</span>
                              <span className="text-theme-text break-words">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </span>
                            </div>
                          ))
                      ) : (
                        <pre className="whitespace-pre-wrap font-sans text-xs">
                          {JSON.stringify(entity.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!searching && results.length === 0 && query && (
        <EmptyState
          icon={<Search className="h-12 w-12" />}
          title="No Results Found"
          description="Try different keywords or adjust your entity type filters"
        />
      )}
    </div>
  );
}

